import React from 'react';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import FadeInSection from '../../components/FadeInSection';
import SkeletonPage from '../../components/SkeletonPage/SkeletonPage';
import coverImg from '../../assets/images/case-cover__ai-native-workflow.png';
import gapImg from '../../assets/images/ai-native__fig-1-gap.png';
import toolchainImg from '../../assets/images/ai-native__fig-2-toolchain.png';
import cycleImg from '../../assets/images/ai-native__fig-3-cycle.png';
import sharedSourceImg from '../../assets/images/ai-native__fig-4-shared-source.png';
import markdownImg from '../../assets/images/ai-native__fig-5-markdown.png';
import qaDiffImg from '../../assets/images/ai-native__fig-6-qa-diff.png';
import './AiNativeWorkflow.scss';

const TextSection = ({ title, children }) => (
  <div className="text-and-image__paragraph ai-native__text-row">
    <FadeInSection>
      <h3 className="text-and-image__title">{title}</h3>
      <div className="ai-native__copy">{children}</div>
    </FadeInSection>
  </div>
);

const Figure = ({ children, caption }) => (
  <FadeInSection>
    <figure className="ai-native__figure">
      <div className="ai-native__figure-body">{children}</div>
      <figcaption>{caption}</figcaption>
    </figure>
  </FadeInSection>
);

const AiNativeWorkflow = () => {
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);

  React.useEffect(() => {
    const img = new Image();
    img.src = coverImg;
    img.onload = () => setIsImageLoaded(true);
    img.onerror = () => setIsImageLoaded(true);
  }, []);

  return (
    <>
      {!isImageLoaded && <SkeletonPage />}
      {isImageLoaded && (
        <section className="ai-native case">
          <ProgressBar />
          <FadeInSection>
            <h1 className="case__title">
              AI-native design workflow. Prototypes built inside the product&apos;s own codebase, so
              designers <span className="case__title-span">feel the real system early</span> and
              design and development build from the same material
            </h1>
          </FadeInSection>

          <div className="case__content ai-native__content">
            <FadeInSection>
              <img className="case__content-image ai-native__cover" src={coverImg} alt="AI-native design workflow" />
            </FadeInSection>

            <TextSection title="The gap that static mocks leave">
              <p>On startup timelines, one step is always cut first: the interactive prototype. There is no time, so design happens in static mocks.</p>
              <p>The quiet result is that a designer never really feels the product until it is built, and two problems follow from that.</p>
              <p>The first is timing. When a mock becomes a real screen, the density, the data shapes, the roles, and the edge cases all appear at once, in production, the most expensive place to meet them.</p>
              <p>The second is language. A mock describes the product in one medium and the code builds it in another, so every handoff is a translation, and things get lost in translation.</p>
            </TextSection>

            <Figure caption="Fig. 1 — Two tracks running in parallel, a gap between them the whole way. They meet only near the end, once the product is built — long after the decisions that shaped it were made.">
              <img className="ai-native__figure-image" src={gapImg} alt="Design and development tracks meeting at the first real contact" />
            </Figure>

            <TextSection title="Prototyping in a live mirror of the product">
              <p>In the agentic era, a designer can build real, working screens inside the product&apos;s own codebase — with its real components, not a picture of them elsewhere.</p>
              <p>The setup matters more than the tool. Built together with engineering, designers get their own copy of an environment that mirrors the live product — kept current by developers, so it stays accurate and is safe to break. Each concept gets its own branch, where <span>an agent (Claude Code, Cursor) builds from the product&apos;s real design tokens</span> and components, not lookalikes. The result is not a picture of the product; made from the same parts, it looks and behaves exactly like the real thing, while staying a prototype.</p>
            </TextSection>

            <Figure caption="Fig. 2 — The shift is in the material. The old handoff described the product in Figma; the new one builds it from the product’s own code and components.">
              <img className="ai-native__figure-image" src={toolchainImg} alt="Shift from Figma to an AI-native product development toolchain" />
            </Figure>

            <TextSection title="Feeling the system early">
              <p>Because <span>the prototype looks and behaves exactly like the product</span> — without being production yet — <span>the surprises arrive at the beginning</span> instead of the end. You click through real data, real states, and real permissions while the idea is still cheap to change. And because each concept lives in a disposable branch, a large restructuring costs an afternoon rather than a sprint, so teams stop quietly avoiding ambitious directions.</p>
              <p>This matters most exactly where products are hardest. In complex, data-heavy systems every flow is dense and particular — its own mix of states, roles, permissions, and edge cases — and none of that can be judged honestly from a static frame.</p>
            </TextSection>

            <Figure caption="Fig. 3 — The step that isn’t ordinary is feel: before any handoff, you can experience the design as a real, working system — clicking through real data, states, and roles while the idea is still cheap to change.">
              <img className="ai-native__figure-image" src={cycleImg} alt="Explore, build, feel, and ship workflow cycle" />
            </Figure>

            <FadeInSection>
              <blockquote className="ai-native__statement"><span>The more complex the product, the less a static mockup can tell you</span> — and the more you need to feel the real thing</blockquote>
            </FadeInSection>

            <TextSection title="Closing the gap between design and development">
              <p>Working in real components changes the handoff before the handoff even happens.</p>
              <p><span>Design and development stop keeping two separate representations</span> of the same thing. Both sides work from the same tokens and the same Storybook components. Some of those components already existed; others were extended during design, and those changes flow back into the shared system.</p>
              <p>There is one source of truth, used in both directions, so handoff becomes continuation instead of translation.</p>
            </TextSection>

            <Figure caption="Fig. 4 — One material, attached at both ends. Design builds with it and extends it; development uses it and ships it. The two stop drifting apart.">
              <img className="ai-native__figure-image" src={sharedSourceImg} alt="Design and development connected by shared tokens and components" />
            </Figure>

            <TextSection title="Handoff becomes flexible">
              <p>A prototype shows how something looks and behaves, but engineering needs more than that to build it for real: the full set of states, edge cases, and error paths, written down.</p>
              <p>In the agentic era the most convenient form for that is <span>a plain Markdown file that lives alongside the code.</span></p>
            </TextSection>

            <FadeInSection><div className="ai-native__benefits">
              <article><b>+</b><strong>Easy to build</strong><p>Quick to write, and quick to change as the design changes — no special tooling.</p></article>
              <article><b>+</b><strong>Easy to track versions</strong><p>It sits next to the code, so its history is simple to follow.</p></article>
              <article><b>+</b><strong>Ready for AI</strong><p>Any assistant can load it to answer questions, walk an edge case, and check whether the build is complete.</p></article>
            </div></FadeInSection>

            <Figure caption="Fig. 5 — Plain text, versioned next to the code, readable by engineers and AI assistants alike. “What’s missing” lives in the document, not in a chat thread.">
              <img className="ai-native__figure-image" src={markdownImg} alt="Markdown behavior specification stored alongside the code" />
            </Figure>

            <TextSection title="QA gets a second, tireless reviewer">
              <p>Once the feature is implemented, two branches describe the same thing: the design prototype and the real build. Hand both to an AI assistant along with the Markdown document, and ask it to compare them. It finds where the implementation drifted from the intended design, and where either side missed a state or an edge case the document promised.</p>
              <p><span>One move covers design QA and engineering QA at once</span> — it makes review faster and cuts down on missed cases and manual checking.</p>
            </TextSection>

            <Figure caption="Fig. 6 — The same feature exists twice, as a prototype and as a build. An AI assistant diffs the two against the document and surfaces where they disagree.">
              <img className="ai-native__figure-image" src={qaDiffImg} alt="Comparison between prototype and implementation" />
            </Figure>

            <TextSection title="This isn't about replacing engineering">
              <p>None of this removes developers from the picture. <span>The prototype is a thinking and communication tool,</span> not a shippable codebase; architecture, state, and the real build stay with engineering.</p>
              <p>The goal is narrower and more honest than replacement: take the guesswork and the translation loss out of the middle, <span>so the same team ships better work, faster.</span></p>
            </TextSection>

            <TextSection title="What it gives, and what it costs">
              <div className="ai-native__tradeoffs">
                <article><b>+</b><strong>Feel the system early</strong><p>Real states, roles, data, and permissions before production.</p></article>
                <article><b>+</b><strong>Prototype in the real material</strong><p>Tokens and components stay aligned with the product.</p></article>
                <article><b>+</b><strong>Design and development grow together</strong><p>One source of truth replaces translation.</p></article>
                <article><b>+</b><strong>QA twice</strong><p>Compare prototype, build, and spec in one pass.</p></article>
                <article><b>−</b><strong>Setup has a cost</strong><p>Engineering must prepare and maintain the mirror environment.</p></article>
                <article><b>−</b><strong>Requires trust</strong><p>Design needs safe access to code and shared working agreements.</p></article>
                <article><b>−</b><strong>Not for every project</strong><p>Simple, low-risk flows may not need this depth.</p></article>
              </div>
            </TextSection>

            <FadeInSection><blockquote className="ai-native__statement ai-native__statement_final">AI-native isn&apos;t a set of tools. It&apos;s a shift in where design happens — inside the product&apos;s real material, <span>early enough to matter, and close enough to engineering that the gap disappears.</span></blockquote></FadeInSection>
          </div>
        </section>
      )}
    </>
  );
};

export default AiNativeWorkflow;
